/**
 * Filing — the GST portal session for this client (FR-E31, D-05; ADR-0037 ND-E1, ND-E2, ND-E12).
 *
 * What is here, in order: whether the lane is on (one sentence when it is not), the client's
 * portal username — masked, and settable — the session's state and its times, and, for the
 * firm's GST manager, *Request OTP* and the OTP form. **Nothing on this screen files a return**;
 * it opens the session that later filing steps (D-07 onward) will use. Below it, the month's
 * filing status (D-13): what was sent to and read from the portal, and what is owed.
 *
 * The OTP goes to the client's registered mobile and email, not to us: the practitioner asks the
 * client for it and types it here. It is sent once, cleared from the form, and kept nowhere.
 *
 * Both session calls need a step-up assertion (ND-E12): when none is in hand the request goes
 * through `IrreversibleConfirm`, which asks for a two-step code first (XC-01). How the client
 * signs at filing — EVC or DSC (ND-E3, D-09) — is set here too.
 */

import { Alert, Button, Card, Input, RadioGroup } from '@fiveox/ui'
import { KeyRound, Send } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { CLIENT, FILING, PERIOD, useEvaluation } from '../lib/access'
import { type FilingState, filing } from '../lib/api'
import { heldAssertion } from '../lib/stepup'
import { useScopedClient } from '../lib/clientScope'
import {
  canAuthenticate,
  laneSentence,
  refusalWords,
  sessionWords,
  usernameWords,
} from '../lib/filingLane'
import { FilingMonth } from './FilingMonth'
import { IrreversibleConfirm } from './IrreversibleConfirm'

/** The two session calls, each needing a step-up assertion. */
type Gated = { kind: 'otp' } | { kind: 'session'; otp: string }

export function Filing() {
  const { client } = useScopedClient()
  const { can } = useEvaluation()
  const [state, setState] = useState<FilingState | null>(null)
  const [username, setUsername] = useState('')
  const [otp, setOtp] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [gated, setGated] = useState<Gated | null>(null)

  /** `quiet` re-reads the state after a refusal without clearing the refusal's sentence. */
  const load = useCallback(async (quiet = false) => {
    if (!client) return
    try {
      setState((await filing.state(client.id)).data)
      if (!quiet) setError(null)
    } catch (e) {
      setError(refusalWords(e))
    }
  }, [client])

  useEffect(() => {
    void load()
  }, [load])

  async function act(run: () => Promise<{ data: FilingState }>) {
    setBusy(true)
    setError(null)
    try {
      setState((await run()).data)
    } catch (e) {
      setError(refusalWords(e))
      void load(true)
    } finally {
      setBusy(false)
    }
  }

  /** Run a session call with the assertion in hand, or ask for one first. */
  function withStepUp(next: Gated) {
    const assertion = heldAssertion()
    if (assertion) void act(() => call(next, assertion))
    else setGated(next)
  }

  function call(next: Gated, assertion: string) {
    if (!client) throw new Error('no client')
    return next.kind === 'otp'
      ? filing.requestOtp(client.id, assertion)
      : filing.openSession(client.id, next.otp, assertion)
  }

  if (!client) return null
  if (!state) {
    return error ? <Alert tone="error">{error}</Alert> : <p className="gst-sub">Loading…</p>
  }

  const off = laneSentence(state)
  const mayAuthenticate = canAuthenticate(state, (resource, verb) =>
    can(resource === 'filing' ? FILING : PERIOD, verb),
  )
  const awaiting = state.session.state === 'awaiting_otp'

  return (
    <div className="space-y-4">
      <div>
        <h1 className="gst-h1">Filing on the GST portal</h1>
        <p className="gst-sub">
          The portal session ProGST holds for this client, and each month&rsquo;s filing status.
          Nothing here files a return.
        </p>
      </div>

      {off && <Alert tone="info">{off}</Alert>}
      {error && <Alert tone="error">{error}</Alert>}

      <Card className="p-4">
        <p className="gst-panel-h mb-2">Portal username</p>
        <p className="mb-3">{usernameWords(state)}</p>
        {state.username_storable && can(CLIENT, 'can_update') && (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              const value = username.trim()
              if (!value) return
              void act(() => filing.setUsername(client.id, value)).then(() => setUsername(''))
            }}
          >
            <Input
              label={state.username_masked ? 'Replace the username' : 'GST portal username'}
              value={username}
              autoComplete="off"
              onChange={(e) => setUsername(e.target.value)}
            />
            <Button type="submit" size="sm" disabled={busy || !username.trim()}>
              Save username
            </Button>
          </form>
        )}
      </Card>

      <Card className="p-4">
        <p className="gst-panel-h mb-2">How this client signs at filing</p>
        {can(CLIENT, 'can_update') ? (
          <RadioGroup
            legend="Signature"
            hideLegend
            options={[
              { value: 'EVC', label: 'EVC', description: 'ProGST files with the OTP the authorised signatory receives.' },
              {
                value: 'DSC',
                label: 'DSC',
                description: 'ProGST saves and submits; the signing is done on the GST portal with the token.',
              },
            ]}
            value={state.signature === 'DSC' ? 'DSC' : 'EVC'}
            onChange={(value) => void act(() => filing.setSignature(client.id, value))}
          />
        ) : (
          <p>{state.signature === 'DSC' ? 'DSC — signed on the GST portal with the token.' : 'EVC — filed through ProGST with the signatory’s OTP.'}</p>
        )}
      </Card>

      <Card className="p-4">
        <p className="gst-panel-h mb-2">Session</p>
        <p className="mb-3">{sessionWords(state)}</p>
        {!off && !mayAuthenticate && (
          <p className="gst-meta">
            Opening a GST portal session is for the firm&rsquo;s GST manager.
          </p>
        )}
        {mayAuthenticate && (
          <div className="space-y-3">
            <Button
              type="button"
              size="sm"
              disabled={busy || !state.username_masked}
              onClick={() => withStepUp({ kind: 'otp' })}
            >
              <Send size={15} /> {awaiting ? 'Request a new OTP' : 'Request OTP'}
            </Button>
            {!state.username_masked && (
              <p className="gst-meta">Save the client&rsquo;s portal username first.</p>
            )}
            {awaiting && (
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault()
                  const typed = otp.trim()
                  setOtp('')
                  withStepUp({ kind: 'session', otp: typed })
                }}
              >
                <Input
                  label="OTP the client received"
                  value={otp}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                />
                <Button type="submit" size="sm" disabled={busy || otp.length < 4}>
                  <KeyRound size={15} /> Open session
                </Button>
              </form>
            )}
          </div>
        )}
      </Card>

      <FilingMonth clientId={client.id} />
      {gated && (
        <IrreversibleConfirm
          open
          onClose={() => setGated(null)}
          title={gated.kind === 'otp' ? 'Request an OTP from the GST portal?' : 'Open the GST portal session?'}
          confirmLabel={gated.kind === 'otp' ? 'Request OTP' : 'Open session'}
          words={refusalWords}
          run={async (assertion) => {
            setState((await call(gated, assertion)).data)
            setGated(null)
          }}
        >
          <p>
            {gated.kind === 'otp'
              ? 'The GST portal sends an OTP to the client’s registered mobile and email; any session open for this client ends.'
              : 'The OTP you typed is sent once to open the session, and kept nowhere.'}
          </p>
        </IrreversibleConfirm>
      )}
    </div>
  )
}
